import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDayInTz, endOfDayInTz, formatDateInputInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { useVisibility } from '@/hooks/useVisibility';

export interface PostcodeMetrics {
  postcode: string;
  suburb: string | null;
  state: string | null;
  totalJobs: number;
  completedJobs: number;
  totalRevenue: number;
  totalLeads: number;
  leadToJobRate: number;
  jobDoneRate: number;
  averageTicket: number;
}

export interface MetroMetrics {
  metro_id: string;
  metro_name: string;
  state: string | null;
  total_leads: number;
  total_jobs: number;
  completed_jobs: number;
  total_revenue: number;
  avg_ticket: number;
  lead_to_job_rate: number;
  job_done_rate: number;
  postcodes: PostcodeMetrics[];
}

export const useMetroReport = (startDate: Date, endDate: Date, timezone: string = DEFAULT_TIMEZONE) => {
  const { canSeeCompanyFinancials } = useVisibility();
  return useQuery({
    enabled: canSeeCompanyFinancials,
    queryKey: ['reports', 'metro-report', startDate, endDate, timezone],
    queryFn: async () => {
      // Use configured timezone for date boundaries
      const start = startOfDayInTz(startDate, timezone).toISOString();
      const end = endOfDayInTz(endDate, timezone).toISOString();
      const startDateStr = formatDateInputInTz(startDate, timezone);
      const endDateStr = formatDateInputInTz(endDate, timezone);

      // Fetch all metros for the company
      const { data: metros, error: metrosError } = await supabase
        .from('metros')
        .select('id, name, state')
        .eq('is_active', true)
        .order('name');

      if (metrosError) throw metrosError;

      // Fetch all jobs for total counts (scheduled in period)
      const { data: allJobs, error: allJobsError } = await supabase
        .from('jobs')
        .select('metro_id, customer_postcode, customer_suburb, customer_state, invoice_total, revenue_before_gst, status, scheduled_date')
        .gte('scheduled_date', start)
        .lte('scheduled_date', end);

      if (allJobsError) throw allJobsError;

      // Fetch leads with metro data
      const { data: leads, error: leadsError } = await supabase
        .from('leads')
        .select('metro_id, customer_postcode, customer_suburb, customer_state, status')
        .gte('created_at', start)
        .lte('created_at', end);

      if (leadsError) throw leadsError;

      // Build metro metrics
      const metroMetrics: MetroMetrics[] = metros?.map(metro => {
        const metroAllJobs = allJobs?.filter(j => j.metro_id === metro.id) || [];
        const metroCompletedJobs = metroAllJobs.filter(j => j.status === 'completed');
        const metroLeads = leads?.filter(l => l.metro_id === metro.id) || [];

        const totalRevenue = metroCompletedJobs.reduce((sum, job) => sum + Number(job.invoice_total || 0), 0);
        const totalJobs = metroAllJobs.length;
        const completedJobs = metroCompletedJobs.length;
        const totalLeads = metroLeads.length;
        const convertedLeads = metroLeads.filter(l => l.status === 'converted').length;

        // Build postcode breakdown within this metro
        const postcodeMap = new Map<string, PostcodeMetrics>();

        metroAllJobs.forEach(job => {
          const postcode = job.customer_postcode || 'Unknown';
          if (!postcodeMap.has(postcode)) {
            postcodeMap.set(postcode, {
              postcode,
              suburb: job.customer_suburb,
              state: job.customer_state,
              totalJobs: 0,
              completedJobs: 0,
              totalRevenue: 0,
              totalLeads: 0,
              leadToJobRate: 0,
              jobDoneRate: 0,
              averageTicket: 0,
            });
          }
          const metrics = postcodeMap.get(postcode)!;
          metrics.totalJobs++;
          if (job.status === 'completed') {
            metrics.completedJobs++;
            metrics.totalRevenue += Number(job.invoice_total || 0);
          }
        });

        metroLeads.forEach(lead => {
          const postcode = lead.customer_postcode || 'Unknown';
          if (!postcodeMap.has(postcode)) {
            postcodeMap.set(postcode, {
              postcode,
              suburb: lead.customer_suburb,
              state: lead.customer_state,
              totalJobs: 0,
              completedJobs: 0,
              totalRevenue: 0,
              totalLeads: 0,
              leadToJobRate: 0,
              jobDoneRate: 0,
              averageTicket: 0,
            });
          }
          const metrics = postcodeMap.get(postcode)!;
          metrics.totalLeads++;
        });

        // Calculate derived metrics for each postcode
        const postcodes = Array.from(postcodeMap.values()).map(p => {
          const postcodeLeads = metroLeads.filter(l => (l.customer_postcode || 'Unknown') === p.postcode);
          const pcConverted = postcodeLeads.filter(l => l.status === 'converted').length;
          return {
            ...p,
            leadToJobRate: p.totalLeads > 0 ? (pcConverted / p.totalLeads) * 100 : 0,
            jobDoneRate: p.totalJobs > 0 ? (p.completedJobs / p.totalJobs) * 100 : 0,
            averageTicket: p.completedJobs > 0 ? p.totalRevenue / p.completedJobs : 0,
          };
        }).sort((a, b) => b.totalRevenue - a.totalRevenue);

        return {
          metro_id: metro.id,
          metro_name: metro.name,
          state: metro.state,
          total_leads: totalLeads,
          total_jobs: totalJobs,
          completed_jobs: completedJobs,
          total_revenue: totalRevenue,
          avg_ticket: completedJobs > 0 ? totalRevenue / completedJobs : 0,
          lead_to_job_rate: totalLeads > 0 ? (convertedLeads / totalLeads) * 100 : 0,
          job_done_rate: totalJobs > 0 ? (completedJobs / totalJobs) * 100 : 0,
          postcodes,
        };
      }) || [];

      // Add "Unassigned" metro for records without metro_id
      const unassignedAllJobs = allJobs?.filter(j => !j.metro_id) || [];
      const unassignedCompletedJobs = unassignedAllJobs.filter(j => j.status === 'completed');
      const unassignedLeads = leads?.filter(l => !l.metro_id) || [];

      if (unassignedAllJobs.length > 0 || unassignedLeads.length > 0) {
        const totalRevenue = unassignedCompletedJobs.reduce((sum, job) => sum + Number(job.invoice_total || 0), 0);
        const totalJobs = unassignedAllJobs.length;
        const completedJobs = unassignedCompletedJobs.length;
        const totalLeads = unassignedLeads.length;
        const convertedLeads = unassignedLeads.filter(l => l.status === 'converted').length;

        // Build postcode breakdown for unassigned
        const postcodeMap = new Map<string, PostcodeMetrics>();

        unassignedAllJobs.forEach(job => {
          const postcode = job.customer_postcode || 'Unknown';
          if (!postcodeMap.has(postcode)) {
            postcodeMap.set(postcode, {
              postcode,
              suburb: job.customer_suburb,
              state: job.customer_state,
              totalJobs: 0,
              completedJobs: 0,
              totalRevenue: 0,
              totalLeads: 0,
              leadToJobRate: 0,
              jobDoneRate: 0,
              averageTicket: 0,
            });
          }
          const metrics = postcodeMap.get(postcode)!;
          metrics.totalJobs++;
          if (job.status === 'completed') {
            metrics.completedJobs++;
            metrics.totalRevenue += Number(job.invoice_total || 0);
          }
        });

        unassignedLeads.forEach(lead => {
          const postcode = lead.customer_postcode || 'Unknown';
          if (!postcodeMap.has(postcode)) {
            postcodeMap.set(postcode, {
              postcode,
              suburb: lead.customer_suburb,
              state: lead.customer_state,
              totalJobs: 0,
              completedJobs: 0,
              totalRevenue: 0,
              totalLeads: 0,
              leadToJobRate: 0,
              jobDoneRate: 0,
              averageTicket: 0,
            });
          }
          const metrics = postcodeMap.get(postcode)!;
          metrics.totalLeads++;
        });

        const postcodes = Array.from(postcodeMap.values()).map(p => {
          const postcodeLeads = unassignedLeads.filter(l => (l.customer_postcode || 'Unknown') === p.postcode);
          const pcConverted = postcodeLeads.filter(l => l.status === 'converted').length;
          return {
            ...p,
            leadToJobRate: p.totalLeads > 0 ? (pcConverted / p.totalLeads) * 100 : 0,
            jobDoneRate: p.totalJobs > 0 ? (p.completedJobs / p.totalJobs) * 100 : 0,
            averageTicket: p.completedJobs > 0 ? p.totalRevenue / p.completedJobs : 0,
          };
        }).sort((a, b) => b.totalRevenue - a.totalRevenue);

        metroMetrics.push({
          metro_id: 'unassigned',
          metro_name: 'Unassigned',
          state: null,
          total_leads: totalLeads,
          total_jobs: totalJobs,
          completed_jobs: completedJobs,
          total_revenue: totalRevenue,
          avg_ticket: completedJobs > 0 ? totalRevenue / completedJobs : 0,
          lead_to_job_rate: totalLeads > 0 ? (convertedLeads / totalLeads) * 100 : 0,
          job_done_rate: totalJobs > 0 ? (completedJobs / totalJobs) * 100 : 0,
          postcodes,
        });
      }

      return metroMetrics.sort((a, b) => b.total_revenue - a.total_revenue);
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
};
